import React, { forwardRef, useCallback, useMemo } from "react";
import { Platform } from "react-native";
import { useHistory } from "react-router-dom";

import { Typography } from "@smartrent/shared-ui";

import type { TypographyProps } from "@smartrent/shared-ui";
import type { Text as RNText, TextProps } from "react-native";
import type { Ref } from "react";

export interface LinkProps extends Omit<TypographyProps, "hrefAttrs"> {
  /**
   * @platform web
   */
  href?: string;
  /**
   * @platform web
   */
  target?: string;
  /**
   * @platform web
   */
  rel?: string;
  /**
   * @platform web
   */
  download?: string;
  /**
   * @platform web
   */
  to?: string;
}

/**
 * An anchor tag component that uses @smartrent/ui's Text component
 *
 * example: https://smartrent-ui.com/components/general/link
 */
export const Link = forwardRef(function Link(
  {
    onPress,
    accessibilityRole = "link",
    href,
    rel,
    target,
    download,
    to,
    ...props
  }: LinkProps,
  ref: Ref<RNText>
) {
  const history = useHistory();
  const linkProps = useMemo(() => {
    return Platform.select<TextProps>({
      web: {
        href: to ? to : href,
        hrefAttrs: {
          rel,
          target,
          download,
        },
      },
    });
  }, [download, href, rel, target, to]);

  const onPressHandler = useCallback(
    (e: any) => {
      if (to) {
        const metaKey = e.metaKey ?? false;
        const ctrlKey = e.ctrlKey ?? false;

        if (!(target == "_blank")) {
          if (metaKey === false && ctrlKey === false) {
            e.preventDefault();
            history.push(to);
          }
        }
      }
      if (onPress) onPress(e);
    },
    [to, target, history, onPress]
  );

  return (
    <Typography
      ref={ref}
      accessibilityRole={accessibilityRole}
      onPress={onPressHandler}
      {...props}
      {...linkProps}
      color="link"
    />
  );
});
